import React, { useState, useCallback, useMemo } from "react";
import { Button, Dropdown, Row, Col } from "react-bootstrap";
import AsModal from "../../components/AsModal";
import ConfirmationDialog from "../../components/ConfirmationDialog";
import PaginatedSearchList from "../../components/PaginatedSearchList";
import EditUserGroupDetails from "./EditUserGroupDetails";
import { parentOptionsFor } from "./EditUserGroupHierarchy";
import { loadUserGroupOptions, UserGroupOption } from "./UserGroupSelection";
import "./AdminUserGroupList.css";
import {
  RowRendererProps,
  renderTable,
} from "../../components/PaginatedListTableRenderer";
import {
  UserGroup,
  GetUserGroupsInput,
  GetUserGroupsOutput,
  CreateUserGroupInput,
} from "../../api/usergroup";
import { ListRendererProps } from "../../components/PaginatedList";
import { anyGranted } from "../../user/user";
import { PageContainer } from "../../components";

const EditUserGroupModal = AsModal(EditUserGroupDetails);

// A group as listed: child groups follow their parent, one level deep.
export interface NestedUserGroup extends UserGroup {
  depth: 0 | 1;
}

const byName = (a: UserGroup, b: UserGroup): number =>
  a.name.localeCompare(b.name);

// nestUserGroups orders groups by name with the children of each group, also
// by name, directly under it. A child whose parent is not listed is shown at
// the top level. The filter is a case-insensitive match on the name; a group
// is kept if it matches, if its parent matches, or if one of its children
// matches, so a matching child is still shown under its parent.
export const nestUserGroups = (
  groups: UserGroup[],
  filter: string
): NestedUserGroup[] => {
  const ids = new Set(groups.map((g) => g.id));
  const isChild = (g: UserGroup): boolean =>
    !!g.parentID && ids.has(g.parentID);
  const childrenOf = new Map<string, UserGroup[]>();
  groups.filter(isChild).forEach((g) => {
    const siblings = childrenOf.get(g.parentID as string) || [];
    childrenOf.set(g.parentID as string, [...siblings, g]);
  });

  const term = filter.trim().toLowerCase();
  const matches = (g: UserGroup): boolean =>
    "" === term || g.name.toLowerCase().includes(term);

  const nested: NestedUserGroup[] = [];
  groups
    .filter((g) => !isChild(g))
    .sort(byName)
    .forEach((parent) => {
      const children = (childrenOf.get(parent.id) || []).sort(byName);
      const shown = matches(parent)
        ? children
        : children.filter((c) => matches(c));
      if (matches(parent) || shown.length > 0) {
        nested.push({ ...parent, depth: 0 });
        shown.forEach((c) => nested.push({ ...c, depth: 1 }));
      }
    });
  return nested;
};

interface UserGroupRowProps extends RowRendererProps<NestedUserGroup> {
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  canEdit?: () => boolean;
  canDelete?: () => boolean;
}

const UserGroupRow: React.FC<UserGroupRowProps> = (props) => {
  const {
    item,
    onEdit,
    onDelete,
    canEdit = (): boolean => false,
    canDelete = (): boolean => false,
  } = props;

  const isChild = item.depth > 0;

  const handleEditUser = useCallback(() => {
    onEdit && onEdit(item.id);
  }, [onEdit, item.id]);

  const handleDeleteUser = useCallback(() => {
    onDelete && onDelete(item.id);
  }, [onDelete, item.id]);

  return (
    <tr data-testid="group-row" data-depth={item.depth}>
      <td className={isChild ? "child" : undefined}>
        {isChild && (
          <span className="child-marker" aria-hidden="true">
            ↳
          </span>
        )}
        {item.name}
      </td>
      <td>{item.desc}</td>
      <td>{item.email}</td>
      <td className="action">
        <Dropdown>
          <Dropdown.Toggle id="dropdown-basic" data-testid="action_menu" />
          <Dropdown.Menu>
            <Dropdown.Item
              key="0"
              disabled={!canEdit()}
              onClick={handleEditUser}
            >
              Edit
            </Dropdown.Item>
            <Dropdown.Item
              key="1"
              disabled={!canDelete()}
              onClick={handleDeleteUser}
            >
              Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </td>
    </tr>
  );
};

const TableRenderer = renderTable(UserGroupRow);

const UserGroupTableRenderer: React.FC<ListRendererProps<NestedUserGroup>> = (
  props
) => (
  <TableRenderer {...props}>
    <div>Name</div>
    <div>Description</div>
    <div>Email</div>
    <div></div>
  </TableRenderer>
);

interface AdminUserGroupListProps {
  getItems: (
    p: GetUserGroupsInput
  ) => Promise<GetUserGroupsOutput> | GetUserGroupsOutput;
  canCreate?: () => boolean;
  canEdit?: () => boolean;
  canDelete?: () => boolean;
  deleteItem: (id: string) => Promise<void> | void;
  createItem: (input: CreateUserGroupInput) => Promise<UserGroup> | UserGroup;
  onEdit: (id: string) => void;
  // loads the groups offered as the parent of a new group
  loadGroupOptions?: (inputValue: string) => Promise<UserGroupOption[]>;
}

const getNewUserGroupFunc = async (): Promise<UserGroup> => {
  return {} as UserGroup;
};

const AdminUserGroupList: React.FC<AdminUserGroupListProps> = (props) => {
  const {
    getItems,
    canCreate = (): boolean => anyGranted("group_create"),
    canEdit = (): boolean => anyGranted("group_update"),
    canDelete = (): boolean => anyGranted("group_delete"),
    deleteItem,
    createItem,
    onEdit,
    loadGroupOptions = loadUserGroupOptions,
  } = props;

  // a new group can be created as the child of any group that is not a child itself
  const parentOptions = useMemo(() => parentOptionsFor("", loadGroupOptions), [
    loadGroupOptions,
  ]);

  // state
  const [showEditUser, setShowEditUser] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteItemId, setDeleteItemID] = useState("");

  // handlers
  const handleDelete = useCallback((id: string) => {
    setDeleteItemID(id);
    setShowConfirmDelete(true);
  }, []);
  const handleConfirmDeleteClosed = useCallback(
    async ({ cancelled = true }) => {
      setShowConfirmDelete(false);
      try {
        if (cancelled === false) {
          await deleteItem(deleteItemId);
          alert("Item deleted");
          setRefresh((p) => p + 1);
        }
      } catch (e) {
        console.log(e);
        alert(e.message);
      } finally {
        setDeleteItemID("");
      }
    },
    [deleteItem, deleteItemId]
  );
  const handleSaveUserGroup = useCallback(
    async ({ parent, ...props }) => {
      try {
        const r = await createItem({
          ...props,
          parentID: (parent as UserGroupOption | undefined)?.value,
        });
        return { userGroup: r };
      } catch (e) {
        console.log(e);
        alert(e.message);
        return { userGroup: {} as UserGroup };
      } finally {
        setRefresh((p) => p + 1);
        setShowEditUser(false);
      }
    },
    [createItem]
  );
  const handleNewUser = useCallback(() => {
    setShowEditUser(true);
  }, []);
  // Every group is loaded, following the pagination to the end, so that each
  // child can be shown under its parent; the filter is applied here, not by
  // the backend, so that the parent of a matching child is still listed.
  const dataSource = useCallback(
    async ({ filter }: { filter: string }) => {
      const groups: UserGroup[] = [];
      let nextKey: string | undefined = undefined;
      do {
        const r: GetUserGroupsOutput = await getItems({ nextKey });
        groups.push(...r.items);
        nextKey = r.nextKey || undefined;
      } while (nextKey);
      return { items: nestUserGroups(groups, filter) };
    },
    [getItems]
  );
  const handleOnHideNewUserGroup = useCallback(
    () => setShowEditUser(false),
    []
  );
  const userGroupListProps = {
    canEdit,
    canDelete,
    onEdit: onEdit,
    onDelete: handleDelete,
  };

  return (
    <PageContainer title="Group Administration">
      <Row>
        <Col />
        <Col md="auto">
          <Button disabled={!canCreate()} onClick={handleNewUser}>
            New
          </Button>
        </Col>
      </Row>
      <hr />
      <Row>
        <Col md={true}>
          <EditUserGroupModal
            title={"New user group"}
            show={showEditUser}
            getUserGroupDetails={getNewUserGroupFunc}
            parentOptions={parentOptions}
            onHide={handleOnHideNewUserGroup}
            onSave={handleSaveUserGroup}
          />
          <PaginatedSearchList
            getItems={dataSource}
            ListRenderer={UserGroupTableRenderer}
            refresh={refresh}
            {...userGroupListProps}
          />
        </Col>
      </Row>
      <ConfirmationDialog
        caption={"Delete user group"}
        message={"Are you sure you want to delete this user group?"}
        show={showConfirmDelete}
        onClose={handleConfirmDeleteClosed}
      />
    </PageContainer>
  );
};

export { UserGroupRow };
export default AdminUserGroupList;
